import { defaultPageSizeOptions, GridViewMetadata } from '../../../grid/gridView/types';
import {
  Column,
  ColumnKeys,
  Dependencies,
  Permissions,
  TaskType,
  getTaskTypeLabel,
  getTaskStatusLabel,
  TasksWebComponentSchema,
  TaskTypesAvailableForCloudAndOnPremisePlatforms,
  TaskTypesAvailableOnlyForCloudPlatform,
  TaskTypesAvailableOnlyForOnPremisePlatform,
  TaskTypesAvailableOnlyForCloudPlatformAndForFeature,
  TaskTypesAvailableOnlyForOnPremisePlatformAndForFeature,
  TaskStatusValues,
  TasksGridEvents,
  TaskStatus,
  ScanTaskScanType,
} from '../types';
import { Feature, ReportType, RELATIVE_TIME_RANGES } from '@nirvana/shared/constants';
import { DataProviderType } from '../../../utils/request';
import { Filter } from '../../../grid/gridFilters/types';
import { ColumnDetails, ColumnType } from '../../../grid/types/common';
import { TasksGridDataProvider } from '../dataProvider/TasksGridDataProvider';
import { html, TemplateResult } from 'lit';
import { TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import moment from 'moment';
import { getLabels } from '../helpers/gridConfigs';

export class GridConfig {
  private filtersUiConfig = [];
  public taskTypeToReportTypeMap: Record<TaskType | number, ReportType>;

  public getDataForView(dependencies: Dependencies): TasksWebComponentSchema {
    const { permissions } = dependencies;
    this.mapReportTasks(dependencies);
    const viewMetaData: GridViewMetadata<Column, ColumnKeys> = {
      viewId: 'network-tasks',
      filters: this.getDefaultFilters(permissions, dependencies.getCurrentUserCompany().id),
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(permissions),
      columns: this.getColumns(),
      pageSize: 100,
      isCompactViewChecked: false,
      hasSelectableRows: true,
      hasPagination: true,
      pageSizeOptions: defaultPageSizeOptions,
      columnRenderers: this.getColumnRenderers(dependencies),
      showGlobalActions: true,
      columnConfigs: [],
      allColumnDetails: this.getColumnDetails(dependencies),
      pageNumber: 1,
      filtersUiConfigs: this.filtersUiConfig,
      dataProvider: new TasksGridDataProvider(dependencies),
      enableCSVExport: false,
      sortColumnName: ColumnKeys.START_DATE,
      sortDirection: 'desc',
    };

    return {
      viewMetaData,
    };
  }

  private getColumnRenderers(dependencies: Dependencies) {
    const { translateFn } = dependencies;
    return {
      [ColumnKeys.COMPANIES]: rowData => {
        const company =
          rowData.item?.companies?.length === 1
            ? rowData.item?.companies[0]?.name
            : rowData.item?.companies?.length + ' ' + 'companies';
        return html` <gz-grid-cell-default-renderer .label="${company}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.TASK_TYPE]: (rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) => {
        const label = getTaskTypeLabel(rowData.item?.type, translateFn)
          ? getTaskTypeLabel(rowData.item?.type, translateFn)
          : translateFn('Unknown');
        return html` <gz-grid-cell-default-renderer .label="${label}"></gz-grid-cell-default-renderer>`;
      },
      [ColumnKeys.STATUS]: (rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) => {
        const status = getTaskStatusLabel(rowData.item?.status, translateFn)
          ? getTaskStatusLabel(rowData.item?.status, translateFn)
          : translateFn('Unknown');
        const progress = '(' + rowData.item.progress + '/' + rowData.item.targetsCount + ')';
        const label = status + ' ' + progress;
        let toolTip = html``;

        if (rowData.item?.hasFailedTasks && rowData.item?.status !== TaskStatus.STATUS_PENDING) {
          const toolTipText = translateFn(
            'The task could not be successfully completed for all objects. Click for details.'
          );
          toolTip = html`<gz-tooltip style="margin-left: 4px" tooltiptext="${toolTipText}" tooltipclass="neutral"
            ><gz-icon name="ds-icon-notification_warning" color="warning" size="20"></gz-icon
          ></gz-tooltip>`;
        }

        return html`<div class="d-flex flex-row align-items-center w-full h-full">
          <gz-tooltip isEllipsis tooltiptext="${label}" tooltipclass="neutral">
            <gz-text
              class="d-inline"
              fontSize="13"
              style="color: var(--blue-layout-400-primary); cursor: pointer"
              @click="${this.onStatusCellClick.bind(this, rowData)}"
              >${label}</gz-text
            ></gz-tooltip
          >${toolTip}
        </div>`;
      },
      [ColumnKeys.PROGRESS]: (rowData, columnDetails: ColumnDetails<Column, ColumnKeys>) => {
        let htmlCode: TemplateResult = html`<span></span>`;
        if (
          typeof this.taskTypeToReportTypeMap[rowData.item?.type] === 'number' &&
          rowData.item?.progress > 0 &&
          rowData.item.ownerId === dependencies.getCurrentUser()._id
        ) {
          // We do not show report button for yara scan
          if (
            rowData.item.type === TaskType.TASK_TYPE_SCAN_PARENT &&
            rowData.item.specifics &&
            rowData.item.specifics.scanType === ScanTaskScanType.YARA
          ) {
            return htmlCode;
          }

          rowData.item.reportType = this.taskTypeToReportTypeMap[rowData.item.type];
          htmlCode = html`<span class="hydragrid-tasks-previewReport-link">
            <gz-icon-button
              buttonFill="quiet"
              buttonColor="secondary"
              buttonType="no_hover"
              icon="ds-icon-export"
              size="32"
              fontSize="24"
              @click="${this.onReportCellClick.bind(this, rowData)}"
            >
            </gz-icon-button>
          </span>`;
        }
        return htmlCode;
      },
      [ColumnKeys.START_DATE]: rowData => {
        return html` <gz-grid-cell-default-renderer
          .label="${moment(rowData?.item?.startDate).format('DD MMM YYYY, HH:mm')}"
        ></gz-grid-cell-default-renderer>`;
      },
    };
  }

  private getDefaultFilters(permissions: Permissions, companyId: string): Array<Filter<ColumnKeys>> {
    let defaultFilters: Array<Filter<ColumnKeys>> = [
      {
        [ColumnKeys.TASK_NAME]: {
          contains: '',
        },
      },
      {
        [ColumnKeys.OWNER]: {
          contains: '',
        },
      },
      {
        [ColumnKeys.TASK_TYPE]: {
          in: [],
        },
      },
      {
        [ColumnKeys.STATUS]: {
          in: [],
        },
      },
      {
        [ColumnKeys.START_DATE]: {
          timeRange: {
            kind: TimeRangeKind.RELATIVE_TIME_RANGE,
            relative: RELATIVE_TIME_RANGES.LAST_7_DAYS,
          },
        },
      },
    ];

    if (permissions.canViewCompanyColumn) {
      defaultFilters = [
        ...defaultFilters,
        {
          [ColumnKeys.COMPANIES]: {
            in: ['all'],
          },
        },
      ];
    }

    return defaultFilters;
  }

  private getPredefinedFiltersKeys(permissions: Permissions): Array<ColumnKeys> {
    let keys = [ColumnKeys.TASK_NAME, ColumnKeys.OWNER, ColumnKeys.TASK_TYPE, ColumnKeys.STATUS, ColumnKeys.START_DATE];

    if (permissions.canViewCompanyColumn) {
      keys = [...keys, ColumnKeys.COMPANIES];
    }

    return keys;
  }

  private getColumns(): Column[] {
    return [
      Column.TASK_NAME,
      Column.OWNER,
      Column.TASK_TYPE,
      Column.STATUS,
      Column.START_DATE,
      Column.COMPANIES,
      Column.PROGRESS,
    ];
  }

  private getColumnDetails(dependencies: Dependencies): Array<ColumnDetails<Column, ColumnKeys>> {
    const { permissions, translateFn } = dependencies;
    const companyId = dependencies.getCurrentUserCompany().id;
    const columnDetails: Array<ColumnDetails<Column, ColumnKeys>> = [
      {
        column: Column.TASK_NAME,
        key: ColumnKeys.TASK_NAME,
        columnName: translateFn('Task name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filterResetValue: { contains: '' },
      },
      {
        column: Column.OWNER,
        key: ColumnKeys.OWNER,
        columnName: translateFn('Owner'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filterResetValue: { contains: '' },
      },
      {
        column: Column.TASK_TYPE,
        key: ColumnKeys.TASK_TYPE,
        columnName: translateFn('Task type'),
        type: ColumnType.ENUM,
        values: getLabels(translateFn, this.getAvailableTasksTypes(dependencies), getTaskTypeLabel),
        removable: false,
        sortable: false,
        filterResetValue: [],
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: translateFn('Status'),
        type: ColumnType.ENUM,
        values: getLabels(translateFn, TaskStatusValues, getTaskStatusLabel),
        removable: true,
        sortable: false,
        filterResetValue: [],
      },
      {
        column: Column.START_DATE,
        key: ColumnKeys.START_DATE,
        columnName: translateFn('Start period'),
        type: ColumnType.DATE,
        removable: false,
        sortable: true,
        showTimePickers: false,
        filterResetValue: {
          timeRange: {
            kind: TimeRangeKind.RELATIVE_TIME_RANGE,
            relative: RELATIVE_TIME_RANGES.LAST_7_DAYS,
          },
        },
      },
      {
        column: Column.PROGRESS,
        key: ColumnKeys.PROGRESS,
        columnName: translateFn('Reports'),
        type: ColumnType.STRING,
        removable: true,
        sortable: false,
        filteringDisabled: true,
      },
    ];

    if (permissions.canViewCompanyColumn) {
      const indexToInsert = 4;
      const deleteCount = 0;
      const columnToInsert: ColumnDetails<Column, ColumnKeys> = {
        column: Column.COMPANIES,
        key: ColumnKeys.COMPANIES,
        columnName: translateFn('Company'),
        type: ColumnType.COMPANY,
        removable: true,
        sortable: true,
        enumFilterCanSelectAll: true,
        showOnlySelectAll: false,
        filterResetValue: ['all'],
      };

      columnDetails.splice(indexToInsert, deleteCount, columnToInsert);
    }

    return columnDetails;
  }

  private getAvailableTasksTypes(dependencies: Dependencies): Array<TaskType> {
    const listOfTaskTypes: Array<TaskType> = [];
    const listOfTaskTypesBasedOnPlatform: Array<TaskType> = dependencies.isCloud()
      ? TaskTypesAvailableOnlyForCloudPlatform
      : TaskTypesAvailableOnlyForOnPremisePlatform;
    const featureTaskTypesMap: Record<Feature | number, Array<TaskType>> = dependencies.isCloud()
      ? TaskTypesAvailableOnlyForCloudPlatformAndForFeature
      : TaskTypesAvailableOnlyForOnPremisePlatformAndForFeature;
    const listOfTaskTypedBasedOnCompanyFeatures: Array<TaskType> = [];
    const userFeatures: number[] = dependencies.getUserFeatures();
    userFeatures.forEach(item => {
      if (featureTaskTypesMap[item]) {
        listOfTaskTypedBasedOnCompanyFeatures.push(...featureTaskTypesMap[item]);
      }
    });
    listOfTaskTypes.push(...TaskTypesAvailableForCloudAndOnPremisePlatforms);
    listOfTaskTypes.push(...listOfTaskTypesBasedOnPlatform);
    listOfTaskTypes.push(...listOfTaskTypedBasedOnCompanyFeatures);

    return listOfTaskTypes;
  }

  private onReportCellClick(rowData, event: CustomEvent) {
    //@ts-ignore
    (event.target as HTMLTextAreaElement)?.getRootNode()?.host?.dispatchEvent(
      new CustomEvent(TasksGridEvents.CLICK_ON_REPORT_ICON, {
        detail: {
          rowData: rowData,
        },
      })
    );
  }

  private onStatusCellClick(rowData, event: CustomEvent) {
    //@ts-ignore
    (event.target as HTMLTextAreaElement)?.getRootNode()?.host?.dispatchEvent(
      new CustomEvent(TasksGridEvents.CLICK_ON_STATUS, {
        detail: {
          rowData: rowData,
        },
      })
    );
  }

  private mapReportTasks(dependencies: Dependencies) {
    this.taskTypeToReportTypeMap = {};
    if (dependencies.isServiceAvailable(DataProviderType.EPS)) {
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_SCAN_PARENT] = ReportType.EPS_REPORT_TYPE_ON_DEMAND_SCAN;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_PATCH_SCAN_PARENT] =
        ReportType.EPS_REPORT_TYPE_ON_DEMAND_PATCH_SCAN;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_PATCH_INSTALL_PARENT] =
        ReportType.EPS_REPORT_TYPE_ON_DEMAND_PATCH_INSTALL;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_PATCH_UNINSTALL_PARENT] =
        ReportType.EPS_REPORT_TYPE_ON_DEMAND_PATCH_UNINSTALL;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_INSTALL_ENDPOINT_PARENT] =
        ReportType.EPS_REPORT_TYPE_ENDPOINT_INSTALLATION;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_INSTALL_ENDPOINT_SUPA_PARENT] =
        ReportType.EPS_REPORT_TYPE_ENDPOINT_INSTALLATION;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_INSTALL_EPS_PARENT] =
        ReportType.EPS_REPORT_TYPE_ENDPOINT_INSTALLATION;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_INSTALL_EPS_SUPA_PARENT] =
        ReportType.EPS_REPORT_TYPE_ENDPOINT_INSTALLATION;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_IOC_SCAN_PARENT] = ReportType.EPS_REPORT_TYPE_IOC_SCAN;
    } else if (dependencies.isServiceAvailable(DataProviderType.SVE)) {
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_SCAN_PARENT] = ReportType.SVE_REPORT_TYPE_ON_DEMAND_SCAN;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_PATCH_SCAN_PARENT] =
        ReportType.SVE_REPORT_TYPE_ON_DEMAND_PATCH_SCAN;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_PATCH_INSTALL_PARENT] =
        ReportType.SVE_REPORT_TYPE_ON_DEMAND_PATCH_INSTALL;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_PATCH_UNINSTALL_PARENT] =
        ReportType.SVE_REPORT_TYPE_ON_DEMAND_PATCH_UNINSTALL;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_INSTALL_ENDPOINT_PARENT] =
        ReportType.SVE_REPORT_TYPE_ENDPOINT_INSTALLATION;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_INSTALL_ENDPOINT_SUPA_PARENT] =
        ReportType.SVE_REPORT_TYPE_ENDPOINT_INSTALLATION;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_INSTALL_EPS_PARENT] =
        ReportType.SVE_REPORT_TYPE_ENDPOINT_INSTALLATION;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_INSTALL_EPS_SUPA_PARENT] =
        ReportType.SVE_REPORT_TYPE_ENDPOINT_INSTALLATION;
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_IOC_SCAN_PARENT] = ReportType.SVE_REPORT_TYPE_IOC_SCAN;
    } else if (dependencies.isServiceAvailable(DataProviderType.MDM)) {
      this.taskTypeToReportTypeMap[TaskType.TASK_TYPE_MDM_SCAN_PARENT] = ReportType.MDM_REPORT_TYPE_ON_DEMAND_SCAN;
    }
  }
}
